import {  t  } from "@/client/libs/dummy-i18n";
import { cn } from "@stackmorph/utils";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { Link, matchRoutes, Outlet, useLocation } from "react-router";

import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useAuthProviders } from "@/client/services/auth/providers";

import { SocialAuth } from "./_components/social-auth";

const authRoutes = [{ path: "/auth/login" }, { path: "/auth/register" }];

export const AuthLayout = () => {
  const location = useLocation();
  const { providers } = useAuthProviders();
  const isAuthRoute = useMemo(() => matchRoutes(authRoutes, location) !== null, [location]);

  if (!providers) return null;

  // Condition (providers.length === 1) hides the divider if providers[] includes only "email"
  const hideDivider = !providers.includes("email") || providers.length === 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="relative min-h-screen w-full bg-gradient-to-br from-background via-background to-secondary/20"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between p-6">
        <Link to="/" className="flex items-center space-x-2">
          <Logo size={40} />
          <span className="text-xl font-bold">StackMorph</span>
        </Link>
        
        <div className="flex items-center space-x-3">
          <LocaleSwitch />
          <ThemeSwitch />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center px-6 py-12">
        <div className="grid w-full max-w-6xl gap-6 lg:grid-cols-2">
          {/* Left Panel - Feature Showcase */}
          <div className="hidden lg:flex lg:flex-col lg:justify-center lg:space-y-8 lg:rounded-3xl lg:border lg:border-border/50 lg:bg-card/50 lg:p-12 lg:backdrop-blur-xl">
            <div className="space-y-4">
              <h2 className="text-4xl font-bold tracking-tight">
                Build Your Professional Resume
              </h2>
              <p className="text-lg text-muted-foreground">
                Create stunning resumes in minutes with our AI-powered builder. No design skills required.
              </p>
            </div>

            <div className="grid gap-4">
              <div className="flex items-start space-x-4 rounded-2xl border border-border/30 bg-background/50 p-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">AI-Powered</h3>
                  <p className="text-sm text-muted-foreground">Smart suggestions for content</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 rounded-2xl border border-border/30 bg-background/50 p-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Beautiful Templates</h3>
                  <p className="text-sm text-muted-foreground">50+ professional designs</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 rounded-2xl border border-border/30 bg-background/50 p-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Privacy First</h3>
                  <p className="text-sm text-muted-foreground">Your data stays private</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Auth Form */}
          <div className="flex flex-col justify-center rounded-3xl border border-border/50 bg-card/80 p-8 backdrop-blur-xl sm:p-12">
            <Outlet />

            {isAuthRoute && (
              <>
                <div className={cn("my-8 flex items-center gap-x-4", hideDivider && "hidden")}>
                  <hr className="flex-1 border-border/50" />
                  <span className="text-xs font-medium text-muted-foreground">
                    {t({
                      message: "or continue with",
                      context:
                        "The user can either login with email/password, or continue with GitHub or Google.",
                    })}
                  </span>
                  <hr className="flex-1 border-border/50" />
                </div>

                <SocialAuth />
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
